import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Pill } from "@/components/shared";

const REVIEW_TONE = { accepted: "success", reviewed: "success", unreviewed: "neutral", in_review: "info", needs_clarification: "warning" };

/**
 * Existing Document Reuse — staff-only, read-only candidate suggestions.
 * requestId present  → full actions incl. "Use existing" (certified confirm → satisfies).
 * requestId absent   → informational (Review only); used pre-creation (Round-2 package builder).
 */
export function ExistingDocumentReuse({ requestId, clientId, documentType, taxYears, jurisdictions, onUsed }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url;
      if (requestId) url = `/document-requests/${requestId}/existing-candidates`;
      else {
        const p = new URLSearchParams({ client_id: clientId, document_type: documentType || "" });
        if (taxYears?.length) p.set("tax_years", taxYears.join(","));
        if (jurisdictions?.length) p.set("jurisdictions", jurisdictions.join(","));
        url = `/document-requests/candidates/by-type?${p.toString()}`;
      }
      const { data } = await api.get(url);
      setCandidates(data.candidates || []);
      setLoaded(true);
    } catch { setCandidates([]); setLoaded(true); }
    finally { setLoading(false); }
  }, [requestId, clientId, documentType, taxYears, jurisdictions]);

  const toggle = () => { const nx = !open; setOpen(nx); if (nx && !loaded) load(); };

  const confirmExisting = async (docId) => {
    if (!requestId) return;
    setConfirming(docId);
    try {
      await api.post(`/documents-intel/${docId}/match/confirm`, { request_id: requestId, role: "primary" });
      toast.success("Existing document confirmed — request satisfied");
      onUsed?.();
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not use existing document"); }
    finally { setConfirming(null); }
  };

  return (
    <div className="mt-1" data-testid={`reuse-${requestId || documentType}`}>
      <button onClick={toggle} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
        data-testid={`reuse-toggle-${requestId || documentType}`}>
        <Icons.Search className="h-3 w-3" />
        {open ? "Hide existing documents" : "Check for existing documents"}
        {loaded && candidates.length > 0 && <Badge variant="secondary" className="text-[9px] ml-1">{candidates.length} found</Badge>}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2" data-testid={`reuse-panel-${requestId || documentType}`}>
          {loading ? (
            <div className="text-xs text-slate-400 flex items-center gap-1"><Icons.Loader2 className="h-3 w-3 animate-spin" />Searching canonical documents…</div>
          ) : candidates.length === 0 ? (
            <div className="text-xs text-slate-400">No existing document found. Continue with the normal request or upload.</div>
          ) : (
            <div className="space-y-2">
              {!requestId && <div className="text-[10px] text-slate-400 italic">Suggestions only. Use these to decide whether to still ask the client. Confirming a match happens on the canonical request after publishing.</div>}
              {candidates.map((c) => (
                <div key={c.document_id} className="rounded-lg border border-slate-200 bg-white p-2" data-testid={`reuse-candidate-${c.document_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800 truncate">{c.title}</div>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <Pill tone={REVIEW_TONE[c.review_status] || "neutral"} className="capitalize">{(c.review_status || "").replace("_", " ")}</Pill>
                        {c.version_status === "superseded" && <Badge variant="outline" className="text-[9px] text-amber-600">superseded v{c.version}</Badge>}
                        {c.tax_years?.length > 0 && <Badge variant="secondary" className="text-[9px]">{c.tax_years.join(", ")}</Badge>}
                        {c.jurisdiction?.length > 0 && <Badge variant="secondary" className="text-[9px]">{c.jurisdiction.join(", ")}</Badge>}
                        {c.uploaded_at && <span className="text-[10px] text-slate-400">{new Date(c.uploaded_at).toLocaleDateString()}</span>}
                        <Badge variant="outline" className="text-[9px]">score {c.match_score}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(c.match_reasons || []).map((r) => <Badge key={r} variant="secondary" className="text-[9px]"><Icons.Check className="h-2.5 w-2.5 mr-0.5 text-emerald-600" />{r}</Badge>)}
                      </div>
                      {c.already_confirmed_elsewhere && (
                        <div className="text-[10px] text-amber-600 mt-1 inline-flex items-center gap-1" data-testid={`reuse-confirmed-elsewhere-${c.document_id}`}>
                          <Icons.Link2 className="h-3 w-3" />Already satisfies “{c.already_confirmed_elsewhere.title || "another request"}”
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <button onClick={() => navigate(`/documents/${c.document_id}`)} className="text-[10px] border border-slate-200 rounded px-2 py-1 hover:bg-slate-50" data-testid={`reuse-review-${c.document_id}`}>Review</button>
                      {requestId && (
                        <button onClick={() => confirmExisting(c.document_id)} disabled={confirming === c.document_id}
                          className="text-[10px] bg-emerald-600 text-white rounded px-2 py-1 disabled:opacity-50" data-testid={`reuse-use-${c.document_id}`}>
                          {confirming === c.document_id ? "…" : "Use existing"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-slate-400">Upload a newer version or replace using the Upload action — the normal request stays available.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
